import type { SaveMemberSignUpRequest } from '__generated__/api/auth';
import { useToast } from '@common/hooks';
import { zodResolver } from '@common/utils';
import { z } from '@core/utils';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';

import { UnauthApi } from '@/api';
import { useAuthStore } from '@/stores/AuthStore';

interface UserInfoFormParams extends SaveMemberSignUpRequest {
  isAgreePrivacy: boolean;
  isAgreeService: boolean;
  codeGender: string;
}

export const MAX_NICKNAME_LENGTH = 10;

const schema = z.object({
  nickname: z
    .string()
    .min(1, '닉네임을 입력해주세요.')
    .min(4, '닉네임은 4자 이상 입력해주세요.')
    .max(
      MAX_NICKNAME_LENGTH,
      `닉네임은 ${MAX_NICKNAME_LENGTH}자 이하로 입력해주세요.`,
    ),
  email: z.email('올바른 이메일을 입력해주세요.'),
  birthDate: z.string({ error: '생년월일을 입력해주세요.' }),
  cellPhone: z.string({ error: '휴대폰번호를 입력해주세요.' }),
  codeGender: z
    .string({ error: '성별을 선택해주세요.' })
    .min(1, '성별을 선택해주세요.'),
  marketingMessageReceiveYN: z.boolean(),
  isAgreePrivacy: z.boolean(),
  isAgreeService: z.boolean(),
});

export const useUserInfoForm = () => {
  const myInfo = useAuthStore((state) => state.myInfo);
  const {
    email,
    birthDate,
    cellPhone,
    nickName,
    codeGender: code_gender,
  } = myInfo ?? {};

  const form = useForm<UserInfoFormParams>({
    resolver: zodResolver<UserInfoFormParams>(schema),
    mode: 'onBlur',
    defaultValues: {
      email: email ?? '',
      birthDate: birthDate ? dayjs(birthDate).format('YYYYMMDD') : '',
      cellPhone: cellPhone ?? '',
      nickname: nickName ?? '',
      codeGender: code_gender ?? '',
      marketingMessageReceiveYN:
        myInfo?.notificationOptions?.marketingMessageReceiveYN ?? false,
      isAgreePrivacy: false,
      isAgreeService: false,
    },
  });

  const { setValue } = form;

  const { toastError, toast } = useToast();

  const { mutate: getNicknameMutate } = useMutation({
    mutationFn: () => UnauthApi['get:/wello/v2/allows/member-wello/nickname'](),

    onSuccess: ({ data }) => {
      const nickname = data?.context?.nickname;

      if (!nickname)
        return toast({
          message: '닉네임을 불러오는데 실패했습니다.',
          type: 'fail',
        });

      setValue('nickname', nickname ?? '');
    },
    onError: (error) =>
      toastError({
        error,
      }),
  });

  return {
    form,
    refetchNickname: getNicknameMutate,
  };
};
